import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BaseModalPageComponent } from '../../../base-modal.component';
import { InstallationBobstContactPluginService } from './bobst-contact.service';
import { ModalComponent } from '../../../modal/modal.component';
import { Person } from '../../../shared/models';
import { maxPhoneLengthValidator } from '../../../utils/maxPhoneLength.directive';
import { BaseInstallationEditPageComponent } from '../base-installation/edit-view.component';
import { Subscription } from 'rxjs';

@Component({
    selector: 'installation-bobst-contact-edit',
    templateUrl: './bobst-contact-edit.component.html',
    standalone: false
})
export class InstallationBobstContactEditPageComponent
  extends BaseInstallationEditPageComponent
  implements OnInit, OnDestroy
{
  plugin: any;
  people: Person[];
  technicien: Person;
  currentItem: any;

  editForm: UntypedFormGroup;
  teamForm: UntypedFormArray;
  @ViewChild('ownerTypeModal', { static: true })
  ownerTypeModal: ModalComponent;
  @ViewChild('peopleModal', { static: true }) peopleModal: ModalComponent;
  @ViewChild('functionModal', { static: true })
  functionModal: ModalComponent;

  private sub = new Subscription();

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected pluginService: InstallationBobstContactPluginService,
    location: Location,
    private cdRef: ChangeDetectorRef
  ) {
    super(location, pluginService, activatedRoute, router);
  }

  ngOnInit(): void {
    this.sub.add(
      this.initRoute().subscribe(() => {
        this.initData();
      })
    );
  }

  initData() {
    this.service.getPlugin(this.inspectionId, this.pluginId).subscribe((data) => {
      this.plugin = data;
      this.initForm();
      this.initPeople();
      this.cdRef.detectChanges();
    });
  }

  initPeople() {
    this.pluginService.getPeople().subscribe((people) => {
      this.people = people.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      this.initTechnicien();
    });
  }

  initTechnicien() {
    const technicienId = this.editForm.controls['TechnicienId'].value;
    if (technicienId) {
      this.technicien = this.people.find((p) => p.id === technicienId);
    }
  }

  initForm() {
    this.teamForm = new UntypedFormArray(this.plugin.config.Team.map((t) => this.initTeam(t)));

    this.editForm = new UntypedFormGroup({
      OwnerType: new UntypedFormControl(this.plugin.config.OwnerType),
      TechnicienId: new UntypedFormControl(this.plugin.config.TechnicienId),
      Team: this.teamForm,
      LocalOfficeTel: new UntypedFormControl(this.plugin.config.LocalOfficeTel, [
        Validators.pattern(/^([+]?\d{1,2}[-| \s]?)?(\d{1,4}[-| ]?){1,4}?(\d{1,4}[-| ]?)\d{1,4}$/),
        Validators.minLength(10),
        maxPhoneLengthValidator(21)
      ])
    });
  }

  initTeam(el?) {
    return new UntypedFormGroup({
      Function: new UntypedFormControl(el?.Function ?? ''),
      Name: new UntypedFormControl(el?.Name ?? ''),
      LastName: new UntypedFormControl(el?.LastName ?? ''),
      Phone: new UntypedFormControl(el?.Phone ?? '', [
        Validators.pattern(/^([+]?\d{1,2}[-| \s]?)?(\d{1,4}[-| ]?){1,4}?(\d{1,4}[-| ]?)\d{1,4}$/),
        Validators.minLength(10),
        maxPhoneLengthValidator(21)
      ]),
      Email: new UntypedFormControl(el?.Email ?? '', Validators.email)
    });
  }

  openOwnerTypeSelector(): void {
    const control = <UntypedFormControl>this.editForm.controls['OwnerType'];
    this.openSelector(this.ownerTypeModal, this.pluginService.getOwnerTypes(), control.value);
  }

  selectOwnerTypeMenu(item: any) {
    const control = <UntypedFormControl>this.editForm.controls['OwnerType'];
    control.setValue(item.selectedIndex);
    control.markAsTouched();
  }

  onOwnerTypeMenuClose() {
    const control = <UntypedFormControl>this.editForm.controls['OwnerType'];
    control.markAsTouched();
  }

  openPeopleSelector(): void {
    const control = <UntypedFormControl>this.editForm.controls['TechnicienId'];
    this.openSelector(
      this.peopleModal,
      this.people.map((person) => ({ label: person.name, value: person.id })),
      this.people.find((p) => p.id === control.value)?.id,
      { search: true, none: true }
    );
  }

  selectPeopleMenu(item: any) {
    const control = <UntypedFormControl>this.editForm.controls['TechnicienId'];
    this.technicien = this.people.find((p) => p.id === item.selectedIndex);
    if (this.technicien?.id === control.value) {
      control.setValue('');
      this.technicien = null;
    } else {
      control.setValue(this.technicien?.id);
    }
  }

  onPeopleMenuClose() {
    const control = <UntypedFormControl>this.editForm.controls['TechnicienId'];
    control.markAsTouched();
  }

  openFunctionSelector(i: number): void {
    this.currentItem = this.teamForm.controls[i];
    this.openSelector(
      this.functionModal,
      this.pluginService.getFunctions(),
      this.currentItem.controls['Function'].value
    );
  }

  selectFunctionMenu(item: any) {
    this.currentItem.controls['Function'].setValue(item.selectedIndex);
    this.currentItem.controls['Function'].markAsTouched();
  }

  onFunctionMenuClose() {
    this.currentItem.controls['Function'].markAsTouched();
  }

  add() {
    this.teamForm.push(this.initTeam());
  }

  remove(i: number) {
    this.teamForm.removeAt(i);
  }

  save(model: any) {
    this.processing = true;
    this.pluginService.updateContact(this.inspectionId, this.pluginId, model).subscribe((el) => {
      this.processing = false;
      this.goToBack();
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
